// Languages for the site. Text is written in English in the code and looked up
// in a dictionary (i18n/es.js) for other languages, falling back to English when
// a line hasn't been translated yet.
import { createContext, Fragment, createElement, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api } from './api';
import { lookup, setCurrentLang } from './langState';

export const LANGUAGES = [
  { id: 'en', label: 'English', short: 'EN', locale: 'en-US' },
  { id: 'es', label: 'Español', short: 'ES', locale: 'es-419' },
];
const LANG_KEY = 'mm-lang';

function readStored() {
  try {
    const v = localStorage.getItem(LANG_KEY);
    return LANGUAGES.some((l) => l.id === v) ? v : null;
  } catch {
    return null;
  }
}

function writeStored(lang) {
  try {
    localStorage.setItem(LANG_KEY, lang);
  } catch {
    // Not remembered in private mode.
  }
}

// The visitor's language: a saved choice, the Spanish homepage address, or their browser's language.
export function detectLanguage() {
  const stored = readStored();
  if (stored) return stored;
  if (typeof window !== 'undefined' && /^\/es(\/|$)/.test(window.location.pathname)) return 'es';
  const prefs = typeof navigator !== 'undefined' ? navigator.languages || [navigator.language] : [];
  return prefs.some((l) => String(l || '').toLowerCase().startsWith('es')) ? 'es' : 'en';
}

function fill(text, vars) {
  if (!vars) return text;
  return text.replace(/\{(\w+)\}/g, (m, k) => (vars[k] !== undefined && vars[k] !== null ? vars[k] : m));
}

export function translate(lang, text, vars) {
  return fill(lookup(lang, text), vars);
}

// Turns "Read the <0>terms</0> and <1>policy</1>" into text with elements:
// parts[0] and parts[1] are functions that wrap the inner text.
export function rich(text, parts) {
  const out = [];
  const re = /<(\d+)>(.*?)<\/\1>/g;
  let last = 0;
  let m;
  let i = 0;
  while ((m = re.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const make = parts[Number(m[1])];
    out.push(createElement(Fragment, { key: i++ }, make ? make(m[2]) : m[2]));
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

const I18nContext = createContext({
  lang: 'en',
  locale: 'en-US',
  setLang: () => {},
  t: (text, vars) => fill(text, vars),
  tr: (text, parts) => rich(text, parts),
});

// Holds the current language. A logged-in member's saved language wins; changes
// are saved to their account so emails can use it too.
export function I18nProvider({ user, onUserChange, children }) {
  const [lang, setLangState] = useState(() => (user && user.language) || detectLanguage());

  // Logging in: use the member's saved language, or save the current one for them.
  useEffect(() => {
    if (!user) return;
    if (user.language && user.language !== lang) setLangState(user.language);
    else if (!user.language) {
      api('/email/preferences', { method: 'PATCH', body: { language: lang } }).then(onUserChange).catch(() => {});
    }
  }, [user && user.id, user && user.language]); // eslint-disable-line react-hooks/exhaustive-deps

  setCurrentLang(lang);
  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const setLang = useCallback((next) => {
    setLangState(next);
    writeStored(next);
    if (user) api('/email/preferences', { method: 'PATCH', body: { language: next } }).then(onUserChange).catch(() => {});
  }, [user, onUserChange]);

  const value = useMemo(() => ({
    lang,
    locale: (LANGUAGES.find((l) => l.id === lang) || LANGUAGES[0]).locale,
    setLang,
    t: (text, vars) => translate(lang, text, vars),
    tr: (text, parts, vars) => rich(translate(lang, text, vars), parts),
  }), [lang, setLang]);

  return createElement(I18nContext.Provider, { value }, children);
}

export function useI18n() {
  return useContext(I18nContext);
}

// A small EN / ES switch.
export function LanguageSwitch({ className = '' }) {
  const { lang, setLang, t } = useI18n();
  return createElement('div', { className: `lang-switch ${className}`, role: 'group', 'aria-label': t('Language') },
    LANGUAGES.map((l) => createElement('button', {
      key: l.id,
      type: 'button',
      className: lang === l.id ? 'active' : '',
      'aria-pressed': lang === l.id,
      title: l.label,
      onClick: () => setLang(l.id),
    }, l.short)));
}
